import { Routes, Route, useNavigate, Link } from 'react-router-dom';
import { useState } from 'react';
import CreateRoom from './components/CreateRoom';
import JoinRoom from './components/JoinRoom';
import PokerRoom from './components/PokerRoom';
import ListRooms from './components/ListRooms';
import SideMenu from './components/SideMenu';
import Sobre from './components/Sobre';
import FAQ from './components/Faq';
import Privacidade from "./components/Privacidade";
import GuiaPlanningPoker from './components/GuiaPlanningPoker';
import RecursosPage from './components/RecursosPage';
import ArtigoRefinamentoBacklog from './components/ArtigoRefinamentoBacklog';
import NavLinks from './components/NavLinks';
import CookieConsent from './components/CookieConsent';
import AuthStatus from './components/AuthStatus';
import RequireAuth from './components/RequireAuth';
import RequireAdmin from './components/RequireAdmin';
import AdminDashboard from './components/AdminDashboard';
import { AuthProvider } from './context/AuthContext';
import Button from './components/ui/Button';
import Card from './components/ui/Card';
import PageContainer from './components/ui/PageContainer';
import './App.css';

function App() {

    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const toggleMenu = () => setMenuOpen(!menuOpen);
    const closeMenu = () => setMenuOpen(false);

    return (
        <AuthProvider>
        <PageContainer>
            <AuthStatus />
            <button
                onClick={toggleMenu}
                className="fixed top-4 left-4 z-[110] p-2.5 bg-surface hover:bg-surface-hover border border-white/20 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 backdrop-blur-md"
                aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
                aria-expanded={menuOpen}
            >
                <svg
                    className="w-6 h-6 text-slate-100"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    {menuOpen ? (
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                    )}
                </svg>
            </button>

            <SideMenu isOpen={menuOpen} onClose={closeMenu} />

            <Link to="/" className="mb-2 cursor-pointer text-center block z-10">
                <img src="/icone_pleini_pouquer.png" alt="Pleini Pouquer Logo" style={{ height: '4cm', width: 'auto' }} className="mx-auto drop-shadow-2xl" />
            </Link>

            <h2 className="text-lg sm:text-xl text-slate-300 mb-8 text-center px-4 z-10">
                🤼🏾‍♂️🎲 Uma forma divertida de fazer estimativas para atividades 🃏💡
            </h2>

            <NavLinks />

            <Routes>
                <Route path="/" element={
                    <div className="w-full max-w-4xl z-10 flex flex-col gap-10 mt-4">
                        <div className="flex flex-col lg:flex-row items-center justify-center w-full gap-8">
                            {/* Bloco de texto e botões */}
                            <Card className="p-8 w-full lg:w-[450px] min-h-[320px] text-center flex flex-col justify-between">
                                <div>
                                    <h3 className="text-xl font-semibold text-slate-100 mb-3">
                                        Pleini Pouquer é uma aplicação de Planning Poker voltada para equipes Ágeis.
                                    </h3>
                                    <p className="text-base text-slate-300 mb-6">
                                        Uma forma fácil e divertida de estimar atividades em grupo.
                                    </p>
                                </div>
                                <div className="flex flex-col gap-4">
                                    <Button
                                        variant="primary"
                                        size="lg"
                                        onClick={() => navigate('/create')}
                                    >
                                        ✨ Criar Sala
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        size="lg"
                                        onClick={() => navigate('/join')}
                                    >
                                        🔑 Entrar em Sala
                                    </Button>
                                </div>
                            </Card>

                            {/* Bloco do GIF */}
                            <Card className="w-full lg:w-[450px] min-h-[320px] flex justify-center items-center p-6">
                                <img
                                    src="/demo_pleini_pouquer.gif"
                                    alt="Demonstração de votação no Planning Poker online Pleini Pouquer"
                                    className="max-h-full max-w-full rounded-lg shadow-lg"
                                />
                            </Card>
                        </div>

                        {/* Conteúdo editorial na home: reforça qualidade do site (AdSense / SEO) — sem anúncios aqui */}
                        <article
                            className="rounded-xl border border-white/10 bg-slate-900/40 p-6 sm:p-8 text-left text-slate-300 leading-relaxed"
                            itemScope
                            itemType="https://schema.org/Article"
                        >
                            <h2 className="text-xl font-bold text-slate-100 mb-4" itemProp="headline">
                                Planning Poker online gratuito para Scrum e times ágeis
                            </h2>
                            <p className="mb-4" itemProp="description">
                                O <strong className="text-slate-200">Pleini Pouquer</strong> é uma ferramenta web
                                para sessões de <strong className="text-slate-200">Planning Poker</strong> (também
                                chamado de Scrum Poker): cada pessoa vota com cartas em segredo e, na revelação,
                                o time discute divergências e chega a um consenso sobre esforço ou complexidade.
                                Funciona bem em retrospectivas, refinamentos de backlog e planejamento de sprint.
                            </p>
                            <p className="mb-4">
                                Diferente de planilhas ou chats genéricos, o foco é a dinâmica de votação
                                simultânea, histórias cadastráveis na sala e papéis (por exemplo, desenvolvedor e
                                QA), alinhando-se ao que equipes ágeis esperam de uma sessão estruturada — sem
                                login com Google para criar ou entrar em salas e uso imediato pelo link da sessão.
                            </p>
                            <p className="mb-4">
                                Para ir além da ferramenta, confira a página{' '}
                                <Link to="/recursos" className="text-sky-400 hover:underline font-medium">
                                    recursos e artigos
                                </Link>
                                {' '}(índice de textos), o{' '}
                                <Link to="/guia" className="text-sky-400 hover:underline font-medium">
                                    guia completo sobre Planning Poker
                                </Link>{' '}
                                com passo a passo e boas práticas, a{' '}
                                <Link to="/faq" className="text-sky-400 hover:underline font-medium">
                                    FAQ
                                </Link>{' '}
                                e a página{' '}
                                <Link to="/sobre" className="text-sky-400 hover:underline font-medium">
                                    sobre o projeto
                                </Link>
                                .
                            </p>
                            <p className="text-sm text-slate-500">
                                Conteúdo informativo original para desenvolvedores, Scrum Masters e Product Owners
                                que buscam estimativas colaborativas com qualidade.
                            </p>
                        </article>
                    </div>
                } />
                <Route path="/create" element={<RequireAuth><CreateRoom /></RequireAuth>} />
                <Route path="/join" element={<RequireAuth><JoinRoom /></RequireAuth>} />
                <Route path="/room/:roomId" element={<RequireAuth><PokerRoom /></RequireAuth>} />
                <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
                <Route path="/rooms" element={<ListRooms />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/sobre" element={<Sobre />} />
                <Route path="/privacidade" element={<Privacidade />} />
                <Route path="/guia" element={<GuiaPlanningPoker />} />
                <Route path="/recursos" element={<RecursosPage />} />
                <Route path="/artigo-refinamento-backlog" element={<ArtigoRefinamentoBacklog />} />
            </Routes>
            <CookieConsent />
        </PageContainer>
        </AuthProvider>
    );
}

export default App;
